import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import MainHeader from "../components/MainHeader";
import { toast } from "sonner";
import { Plus, Trash2, Save, Eye, EyeOff, Star } from "lucide-react";

const EMPTY = {
  title: "",
  tagline: "",
  synopsis: "",
  director: "Mira Aleson",
  year: 2026,
  runtime_minutes: 90,
  rating: "NR",
  genre: "",
  cast: "",
  poster_url: "",
  backdrop_url: "",
  trailer_url: "",
  video_url: "",
  rent_price: 4.99,
  buy_price: 14.99,
  rent_window_hours: 48,
  is_now_showing: false,
  coming_soon: false,
  release_date: "",
  filmmaker_slug: "mira-aleson",
  run_start_date: "",
  run_weeks: 4,
};

function toPayload(f) {
  return {
    ...f,
    cast: typeof f.cast === "string" ? f.cast.split(",").map((s) => s.trim()).filter(Boolean) : f.cast,
    year: Number(f.year),
    runtime_minutes: Number(f.runtime_minutes),
    rent_price: Number(f.rent_price),
    buy_price: Number(f.buy_price),
    rent_window_hours: Number(f.rent_window_hours),
    quotes: f.quotes || [],
  };
}

function fromFilm(film) {
  return { ...film, cast: (film.cast || []).join(", ") };
}

export default function Admin() {
  const { user } = useAuth();
  const [films, setFilms] = useState([]);
  const [editing, setEditing] = useState(null); // slug or "new"
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  useEffect(() => { document.body.classList.remove("theme-mini"); }, []);

  const load = () => api.get("/films").then(({ data }) => setFilms(data));
  useEffect(() => { if (user && user.role === "admin") load(); }, [user]);

  if (user === null) return null;
  if (!user || user.role !== "admin") return <Navigate to="/" replace />;

  const startNew = () => { setForm(EMPTY); setEditing("new"); };
  const startEdit = (f) => { setForm(fromFilm(f)); setEditing(f.slug); };
  const cancel = () => { setEditing(null); setForm(EMPTY); };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing === "new") {
        await api.post("/films", toPayload(form));
        toast.success("Film added to the marquee.");
      } else {
        await api.patch(`/films/${editing}`, toPayload(form));
        toast.success("Film updated.");
      }
      cancel();
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (slug) => {
    if (!window.confirm("Remove this film from the marquee?")) return;
    try {
      await api.delete(`/films/${slug}`);
      toast.success("Film removed.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleNowShowing = async (f) => {
    try {
      await api.patch(`/films/${f.slug}`, toPayload({ ...fromFilm(f), is_now_showing: !f.is_now_showing }));
      toast.success(!f.is_now_showing ? "Now showing." : "Pulled from marquee.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#F4F2EC]">
      <MainHeader />
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-12">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gs-ember">Projection booth</p>
            <h1 className="font-outfit font-semibold text-4xl text-gs-ink mt-2">Film Library</h1>
            <p className="font-sans text-sm text-gs-ink/60 mt-2">Manage what plays on the Green Screen.</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex gap-3 font-mono text-[10px] uppercase tracking-widest">
              <span className="text-gs-ember">Films</span>
              <a href="/admin/sales" className="text-gs-dust hover:text-gs-ink">Sales</a>
              <a href="/admin/filmmakers" className="text-gs-dust hover:text-gs-ink">Filmmakers</a>
            </div>
            <button onClick={startNew} data-testid="add-film-btn" className="bg-gs-ink text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-gs-ink/80 inline-flex items-center gap-2">
              <Plus size={14} /> Add Film
            </button>
          </div>
        </div>

        {editing && (
          <form onSubmit={save} data-testid="film-form" className="bg-white border border-black/5 rounded-xl p-6 mb-10 grid md:grid-cols-2 gap-5 shadow-sm">
            <Field label="Title" name="title" form={form} setForm={setForm} required />
            <Field label="Tagline" name="tagline" form={form} setForm={setForm} />
            <Field label="Director" name="director" form={form} setForm={setForm} required />
            <Field label="Year" name="year" type="number" form={form} setForm={setForm} required />
            <Field label="Runtime (min)" name="runtime_minutes" type="number" form={form} setForm={setForm} required />
            <Field label="Rating" name="rating" form={form} setForm={setForm} />
            <Field label="Genre" name="genre" form={form} setForm={setForm} />
            <Field label="Cast (comma-separated)" name="cast" form={form} setForm={setForm} />
            <Field label="Poster URL" name="poster_url" form={form} setForm={setForm} required />
            <Field label="Backdrop URL" name="backdrop_url" form={form} setForm={setForm} required />
            <Field label="Trailer URL" name="trailer_url" form={form} setForm={setForm} />
            <Field label="Video URL (mock)" name="video_url" form={form} setForm={setForm} />
            <Field label="Rent price" name="rent_price" type="number" step="0.01" form={form} setForm={setForm} />
            <Field label="Buy price" name="buy_price" type="number" step="0.01" form={form} setForm={setForm} />
            <Field label="Rent window (hours)" name="rent_window_hours" type="number" form={form} setForm={setForm} />
            <Field label="Release date label" name="release_date" form={form} setForm={setForm} />
            <Field label="Filmmaker slug" name="filmmaker_slug" form={form} setForm={setForm} />
            <Field label="Run start date (YYYY-MM-DD)" name="run_start_date" form={form} setForm={setForm} />
            <Field label="Run weeks" name="run_weeks" type="number" form={form} setForm={setForm} />
            <div className="md:col-span-2">
              <label className="block font-mono text-[10px] uppercase tracking-widest text-gs-dust mb-2">Synopsis</label>
              <textarea
                value={form.synopsis}
                onChange={(e) => setForm({ ...form, synopsis: e.target.value })}
                required
                rows={4}
                data-testid="synopsis-input"
                className="w-full bg-white border border-gs-line rounded-md px-3 py-2 font-sans text-sm focus:outline-none focus:border-gs-ember"
              />
            </div>
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" data-testid="now-showing-checkbox" checked={!!form.is_now_showing} onChange={(e) => setForm({ ...form, is_now_showing: e.target.checked })} />
              Now showing
            </label>
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" data-testid="coming-soon-checkbox" checked={!!form.coming_soon} onChange={(e) => setForm({ ...form, coming_soon: e.target.checked })} />
              Coming soon
            </label>
            <div className="md:col-span-2 flex items-center gap-3 pt-2">
              <button type="submit" disabled={busy} data-testid="save-film-btn" className="bg-gs-ink text-gs-cream rounded-none px-5 py-2 text-xs font-mono uppercase tracking-[0.25em] border border-gs-ember/50 hover:bg-gs-ember hover:text-gs-ink inline-flex items-center gap-2">
                <Save size={14} /> {busy ? "Saving…" : "Save"}
              </button>
              <button type="button" onClick={cancel} className="text-sm text-gs-dust hover:text-gs-ink">Cancel</button>
            </div>
          </form>
        )}

        <div className="grid md:grid-cols-2 gap-4">
          {films.map((f, i) => (
            <div key={f.id} data-testid={`admin-film-${i}`} className="bg-white border border-black/5 rounded-xl overflow-hidden flex shadow-sm">
              <div className="w-28 aspect-[3/4] bg-gs-ink shrink-0">
                <img src={f.poster_url} alt={f.title} className="w-full h-full object-cover" />
              </div>
              <div className="p-4 flex-1 flex flex-col">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-outfit font-semibold text-xl text-gs-ink">{f.title}</p>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-gs-dust">{f.year} · {f.runtime_minutes}m · {f.rating}</p>
                  </div>
                  <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest">
                    {f.is_now_showing ? <span className="text-gs-ember"><Star size={10} className="inline" /> on screen</span> : f.coming_soon ? <span className="text-gs-marigold">soon</span> : <span className="text-gs-dust">archive</span>}
                  </span>
                </div>
                <p className="font-sans text-xs text-gs-ink/60 mt-2 line-clamp-2">{f.synopsis}</p>
                <div className="flex items-center gap-2 mt-auto pt-3">
                  <button onClick={() => toggleNowShowing(f)} className="text-xs inline-flex items-center gap-1 text-gs-ink hover:text-gs-ember">
                    {f.is_now_showing ? <EyeOff size={12} /> : <Eye size={12} />} {f.is_now_showing ? "Pull" : "Show"}
                  </button>
                  <button onClick={() => startEdit(f)} className="text-xs text-gs-ink hover:text-gs-ember">Edit</button>
                  <button onClick={() => remove(f.slug)} className="text-xs inline-flex items-center gap-1 text-gs-dust hover:text-red-700 ml-auto">
                    <Trash2 size={12} /> Remove
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Field({ label, name, type = "text", step, form, setForm, required }) {
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-widest text-gs-dust mb-2">{label}</label>
      <input
        type={type}
        step={step}
        value={form[name]}
        onChange={(e) => setForm({ ...form, [name]: type === "number" ? e.target.value : e.target.value })}
        required={required}
        data-testid={`field-${name}`}
        className="w-full bg-white border border-gs-line rounded-md px-3 py-2 font-sans text-sm focus:outline-none focus:border-gs-ember"
      />
    </div>
  );
}
